/**
 * One row per mailbox for the unified "Connect mailboxes" panel.
 *
 * Three sources have to agree before an admin can answer "why is my mailbox not
 * listed?", and previously none of them were shown together:
 *
 *   1. `GET /api/inboxes/mailboxes` — mailboxes we hold credentials for, either
 *      provider, each carrying the inboxes it feeds.
 *   2. `GET /oauth/{provider}/mailbox-access/requests` — consent invites that
 *      have been sent but not yet approved. These have no mailbox row at all
 *      yet, so a panel reading only (1) shows nothing while an owner is sitting
 *      on an approval email.
 *   3. Each inbox's `member_smtps` — addresses an admin typed into the Members
 *      tab that have never been requested. Reading only (1) and (2) makes these
 *      vanish, which is the state that reads as "I added it and nothing
 *      happened".
 *
 * Merged here rather than in the component so the precedence is testable. A
 * real credential always wins over a pending invite, and a pending invite
 * always wins over a bare member entry — otherwise a mailbox that finished
 * consenting would keep rendering as "Request Sent" because the stale
 * control-plane row outlived the grant.
 */

/** Status ranking — higher wins when the same address appears twice. */
const SOURCE_RANK = { credential: 3, request: 2, member: 1 };

function normalise(email) {
    return String(email || '')
        .toLowerCase()
        .trim();
}

/**
 * @param {object} args
 * @param {Array}  args.directory      Rows from `GET /api/inboxes/mailboxes`.
 * @param {Array}  args.accessRequests Control-plane access requests (both providers).
 * @param {Array}  args.inboxes        Inbox documents, for `member_smtps` and names.
 * @returns {Array} one row per address, sorted by address
 */
export function mergeMailboxRows({ directory = [], accessRequests = [], inboxes = [] } = {}) {
    const byEmail = new Map();

    const put = (email, row, source) => {
        const key = normalise(email);
        if (!key) return;
        const existing = byEmail.get(key);
        if (existing && SOURCE_RANK[existing._source] >= SOURCE_RANK[source]) return;
        byEmail.set(key, { ...row, email: key, _source: source });
    };

    const inboxList = Array.isArray(inboxes) ? inboxes : [];
    const inboxName = id => inboxList.find(i => i?.inbox_id === id)?.name || '';

    // ── 3. Bare member entries (lowest precedence) ──────────────────────────
    inboxList.forEach(inbox => {
        const members = Array.isArray(inbox?.member_smtps) ? inbox.member_smtps : [];
        members.forEach(mail => {
            put(
                mail,
                {
                    key: `member:${normalise(mail)}`,
                    provider: undefined,
                    status: 'none',
                    connection_id: null,
                    mail: normalise(mail),
                    inboxes: [{ inbox_id: inbox.inbox_id, name: inbox.name || '' }],
                    delivery_live: false,
                },
                'member'
            );
        });
    });

    // ── 2. Pending consent invites ──────────────────────────────────────────
    const requests = Array.isArray(accessRequests) ? accessRequests : [];
    requests
        .filter(r => r?.status === 'pending' || r?.status === 'sent')
        .forEach(r => {
            const inboxId = r.inboxId || r.inbox_id || '';
            put(
                r.mailboxEmail,
                {
                    key: `request:${normalise(r.mailboxEmail)}`,
                    provider: r.provider,
                    status: 'pending',
                    connection_id: null,
                    mail: normalise(r.mailboxEmail),
                    inboxes: inboxId
                        ? [{ inbox_id: inboxId, name: inboxName(inboxId) }]
                        : [],
                    delivery_live: false,
                    sent_at: r.sentAt,
                    expires_at: r.expiresAt,
                },
                'request'
            );
        });

    // ── 1. Real credentials (highest precedence) ────────────────────────────
    (Array.isArray(directory) ? directory : []).forEach(row => {
        put(row?.email, row, 'credential');
    });

    return [...byEmail.values()].sort((a, b) => a.email.localeCompare(b.email));
}

/**
 * Counts for the panel's stat cards.
 *
 * `reauth` is counted separately rather than folded into `revoked`. A mailbox
 * needing re-consent still has an inbox membership and a place in the flow; a
 * revoked one does not, and the remedies differ.
 */
export function mailboxCounts(rows = []) {
    const list = Array.isArray(rows) ? rows : [];
    return {
        active: list.filter(r => r.status === 'active').length,
        pending: list.filter(r => r.status === 'pending').length,
        reauth: list.filter(r => r.status === 'reauth' || r.status === 'expired').length,
        revoked: list.filter(r => r.status === 'revoked' || r.status === 'none').length,
    };
}

/**
 * Which stat card a row belongs to, so clicking a card filters to it.
 *
 * Kept beside `mailboxCounts` on purpose: when the two disagree a card shows a
 * count that its own filter cannot reproduce, and the list renders empty under a
 * non-zero number.
 */
export function mailboxBucket(row) {
    const status = row?.status;
    if (status === 'active') return 'active';
    if (status === 'pending') return 'pending';
    if (status === 'reauth' || status === 'expired') return 'reauth';
    return 'revoked';
}
